import {
  doc, getDoc, getDocs, collection, query, where, limit,
  increment, runTransaction, serverTimestamp, documentId,
  type DocumentReference, type Transaction,
} from 'firebase/firestore';
import { db } from '../firebase';
import { computeNewHalfLife, computeNewDifficulty } from '../tamkeenEngine';
import { captureError } from '../monitoring';
import type {
  CardState,
  CardStateWithId,
  CardStateMap,
  CardMapCache,
  RecordAttemptResult,
  RecordAttemptOptions,
} from '../../types/progress';

// ─── Refs ───────────────────────────────────────────

function cardRef(uid: string, cardId: string): DocumentReference {
  return doc(db, 'progress', uid, 'cards', cardId);
}

function deckRef(uid: string, deckId: string): DocumentReference {
  return doc(db, 'progress', uid, 'decks', deckId);
}

// ─── Runtime sanitizer for Firestore CardState ──────

export function sanitizeCardState(raw: Record<string, unknown>): CardState {
  return {
    d: typeof raw.d === 'string' ? raw.d : '',
    c: typeof raw.c === 'number' ? raw.c : 0,
    w: typeof raw.w === 'number' ? raw.w : 0,
    s: typeof raw.s === 'number' ? raw.s : 0,
    f: (raw.f === 'c' || raw.f === 'w') ? raw.f : 'c',
    t: raw.t ?? null,
    ...(typeof raw.h === 'number' ? { h: raw.h } : {}),
    ...(typeof raw.di === 'number' ? { di: raw.di } : {}),
  };
}

// ═══════════════════════════════════════════════════════
// Card State
//
// Document shape: { d, c, w, s, f, t }
//   d = deckId          (enables query-by-deck)
//   c = correct count   (lifetime)
//   w = wrong count     (lifetime)
//   s = current streak  (consecutive correct, resets on wrong)
//   f = first-ever attempt result ("c" | "w")
//   t = last interaction timestamp
// ═══════════════════════════════════════════════════════

export async function getCardState(uid: string, cardId: string): Promise<CardState | null> {
  const snap = await getDoc(cardRef(uid, cardId));
  return snap.exists() ? sanitizeCardState(snap.data()) : null;
}

export async function getCardStates(uid: string, cardIds: string[]): Promise<CardStateMap> {
  if (!cardIds?.length) return {};
  const result: CardStateMap = {};
  const BATCH = 30;
  const cardsCol = collection(db, 'progress', uid, 'cards');
  const promises: ReturnType<typeof getDocs>[] = [];
  for (let i = 0; i < cardIds.length; i += BATCH) {
    const batch = cardIds.slice(i, i + BATCH);
    promises.push(getDocs(query(cardsCol, where(documentId(), 'in', batch))));
  }
  const snaps = await Promise.all(promises);
  snaps.forEach(snap => snap.docs.forEach((d) => { result[d.id] = sanitizeCardState(d.data()); }));
  return result;
}

export async function getAllCardStates(uid: string, maxCards: number = 2000): Promise<CardStateWithId[]> {
  const col = collection(db, 'progress', uid, 'cards');
  const q = query(col, limit(maxCards));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...sanitizeCardState(d.data()) }));
}

let _cardMapCache: CardMapCache = { uid: null, data: null, ts: 0 };
const CARD_MAP_CACHE_TTL = 30_000;

const CARD_MAP_MAX = 3000;

export async function getAllCardStatesMap(uid: string): Promise<CardStateMap> {
  if (_cardMapCache.uid === uid && Date.now() - _cardMapCache.ts < CARD_MAP_CACHE_TTL) {
    return _cardMapCache.data!;
  }
  const col = collection(db, 'progress', uid, 'cards');
  const snap = await getDocs(query(col, limit(CARD_MAP_MAX)));
  const map: CardStateMap = {};
  snap.docs.forEach(d => { map[d.id] = sanitizeCardState(d.data()); });
  _cardMapCache = { uid, data: map, ts: Date.now() };
  return map;
}

export function invalidateCardMapCache(): void {
  _cardMapCache = { uid: null, data: null, ts: 0 };
}

export async function getCardStatesForDeck(uid: string, deckId: string): Promise<CardStateMap> {
  const q = query(
    collection(db, 'progress', uid, 'cards'),
    where('d', '==', deckId),
  );
  const snap = await getDocs(q);
  const result: CardStateMap = {};
  snap.docs.forEach((d) => { result[d.id] = sanitizeCardState(d.data()); });
  return result;
}

export async function recordAttempt(
  uid: string,
  cardId: string,
  deckId: string,
  isCorrect: boolean,
  totalCards: number | null = null,
  { resetOnCorrect = false }: RecordAttemptOptions = {},
): Promise<RecordAttemptResult> {
  try {
    const cRef = cardRef(uid, cardId);
    const dRef = deckId != null ? deckRef(uid, deckId) : null;
    const now = serverTimestamp();

    const isFirstEver = await runTransaction(db, async (tx: Transaction) => {
      const snap = await tx.get(cRef);

      if (!snap.exists()) {
        const newDoc: Record<string, unknown> = {
          d: deckId,
          c: isCorrect ? 1 : 0,
          w: isCorrect ? 0 : 1,
          s: isCorrect ? 1 : 0,
          f: isCorrect ? 'c' : 'w',
          t: now,
        };
        if (resetOnCorrect) {
          const newDi = computeNewDifficulty(null, isCorrect);
          newDoc.h = computeNewHalfLife(null, isCorrect, null, newDi);
          newDoc.di = newDi;
        }
        tx.set(cRef, newDoc);

        if (dRef != null && totalCards != null) {
          tx.set(dRef, { seen: increment(1), total: totalCards }, { merge: true });
        }
        return true;
      }

      const data = sanitizeCardState(snap.data());
      const updates: Record<string, unknown> = {
        s: isCorrect ? (data.s || 0) + 1 : 0,
        t: now,
      };
      if (isCorrect) {
        updates.c = increment(1);
      } else {
        updates.w = increment(1);
      }
      if (resetOnCorrect) {
        const rawT = data.t as { toDate?: () => Date } | number | null;
        const lastMs = rawT && typeof rawT === 'object' && 'toDate' in rawT
          ? rawT.toDate!().getTime()
          : (typeof rawT === 'number' ? rawT : null);
        const deltaDays = lastMs ? (Date.now() - lastMs) / (24 * 60 * 60 * 1000) : null;
        const newDi = computeNewDifficulty(data.di ?? null, isCorrect);
        updates.h = computeNewHalfLife(data.h ?? null, isCorrect, deltaDays, newDi);
        updates.di = newDi;
      }

      tx.update(cRef, updates);
      return false;
    });

    _cardMapCache = { uid: null, data: null, ts: 0 };
    return { isFirstEver };
  } catch (err) {
    captureError(err, { operation: 'recordAttempt', uid, cardId, deckId, isCorrect });
    throw err;
  }
}
